// Guided set-up (proposed steps 0.35-0.39; docs/specs/GUIDED_SETUP_SCREENS.md): the shape crm_setup_state returns,
// read tolerantly (a missing key never breaks a screen), where to resume, the six steps' status, and what the system
// map shows. Pure functions, shared by the screens, the demo and the tests.
import { MODULE_BY_KEY, MODULE_KEYS, MODULES, isModuleKey, isPriorityKey, type JourneyStepKey, type ModuleKey, type ModuleState, type PartStatus, type PriorityKey, type SetupStepKey } from "./setupCatalogue";
import { readPlaySignals, type PlaySignals } from "./signals";
import type { Json, JsonObject } from "./types";

export interface CoreFields {
  what_we_sell: string;
  who_we_serve: string;
  proof: string[];
  voice: { tone: string; examples: string[] };
  words_to_avoid: string[];
  website: string;
  business_type: string;
  sources: Record<string, string>;
  confidence: Record<string, "high" | "medium" | "low">;
  confirmed_at?: string | null;
  confirmed_by?: string | null;
}

export interface RouteChoice {
  goal: string | null;
  first_module: ModuleKey;
  source: "owner" | "suggested";
  reason: string;
}

export interface PartView {
  on: boolean;
  status: PartStatus;
}

export interface ModuleView {
  /** The owner's decision if they made one, else Ogma's suggestion (a business from before guided set-up: on). */
  state: ModuleState;
  reason: string;
  parts: Record<string, PartView>;
  /** Ogma suggested this state (from the website or the goal); the owner hasn't decided yet. */
  suggested?: boolean;
  /** The owner decided this state themselves. */
  decided?: boolean;
}

export type PlayStatus = "draft" | "proposed" | "approved" | "retired";

export interface PlaySummary {
  id: string;
  module: ModuleKey;
  name: string;
  status: PlayStatus;
  audience: PlayAudience;
}

export interface PlayAudience {
  who: string;
  where: string;
  size: string;
  segment_keys: string[];
  notes: string;
}

export interface Play extends PlaySummary {
  card: JsonObject | null;
  angles: { key: string; label: string; line: string }[];
  cold_test: JsonObject | null;
  approved_at?: string | null;
  created_at?: string;
  updated_at?: string;
  /** A play the migration made for a business that already ran: it shows the settings in use. */
  mirrors_settings?: boolean;
  decision_note?: string | null;
  /** 2.35 The roles this play could replace (docs/specs/SIGNALS.md). */
  signals?: PlaySignals;
}

export interface NextStep {
  step: SetupStepKey;
  module: ModuleKey | null;
  part: string | null;
}

export interface SetupView {
  core: CoreFields;
  core_confirmed: boolean;
  route: RouteChoice | null;
  modules: Record<ModuleKey, ModuleView>;
  plays: PlaySummary[];
  next: NextStep;
  live: boolean;
  /** A business that ran before guided set-up and has made no module decisions yet: every module shows on. */
  legacy?: boolean;
  /** Set-up v2: the owner chose the route (a priority, or a v1 goal). */
  route_confirmed?: boolean;
  /** Set-up v2: the priority the owner chose, if any. */
  priority?: PriorityKey | null;
  /** Set-up v2: what the website reading suggested (never applied). */
  priority_suggestion?: PrioritySuggestion | null;
  /** Set-up v2: where the four-step journey is. */
  journey?: Journey;
}

export interface PrioritySuggestion {
  priority: PriorityKey;
  reason: string;
  /** Who suggested it: Claude, or the simple rules when Claude wasn't available. */
  by: "claude" | "rules";
}

export interface Journey {
  /** done: gone live, or a business from before guided set-up (never sent back to set-up). */
  step: JourneyStepKey | "done";
  module: ModuleKey | null;
}

export interface Sample {
  id: string;
  kind: string;
  play_id: string | null;
  payload: JsonObject;
  created_at: string;
}

// ---------------------------------------------------------------------------- reading tolerantly
const isObj = (v: unknown): v is JsonObject => typeof v === "object" && v !== null && !Array.isArray(v);
const str = (v: unknown): string => (typeof v === "string" ? v : "");
const strs = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "") : []);
const STATES: ModuleState[] = ["on", "off", "later"];
const PART_STATUSES: PartStatus[] = ["not_started", "set_up", "live"];

export function emptyCore(): CoreFields {
  return { what_we_sell: "", who_we_serve: "", proof: [], voice: { tone: "", examples: [] }, words_to_avoid: [], website: "", business_type: "", sources: {}, confidence: {} };
}

export function readCore(v: unknown): CoreFields {
  const c = isObj(v) ? v : {};
  const voice = isObj(c.voice) ? c.voice : {};
  const conf: CoreFields["confidence"] = {};
  if (isObj(c.confidence)) for (const [k, x] of Object.entries(c.confidence)) if (x === "high" || x === "medium" || x === "low") conf[k] = x;
  const sources: Record<string, string> = {};
  if (isObj(c.sources)) for (const [k, x] of Object.entries(c.sources)) if (typeof x === "string") sources[k] = x;
  return {
    what_we_sell: str(c.what_we_sell),
    who_we_serve: str(c.who_we_serve),
    proof: strs(c.proof),
    voice: { tone: str(voice.tone), examples: strs(voice.examples) },
    words_to_avoid: strs(c.words_to_avoid),
    website: str(c.website),
    business_type: str(c.business_type),
    sources,
    confidence: conf,
    confirmed_at: typeof c.confirmed_at === "string" ? c.confirmed_at : null,
    confirmed_by: typeof c.confirmed_by === "string" ? c.confirmed_by : null,
  };
}

export function readAudience(v: unknown): PlayAudience {
  const a = isObj(v) ? v : {};
  return { who: str(a.who), where: str(a.where), size: str(a.size), segment_keys: strs(a.segment_keys), notes: str(a.notes) };
}

function readPlaySummary(v: unknown): PlaySummary | null {
  if (!isObj(v) || typeof v.id !== "string" || !isModuleKey(v.module)) return null;
  const status = (["draft", "proposed", "approved", "retired"] as PlayStatus[]).includes(v.status as PlayStatus) ? (v.status as PlayStatus) : "draft";
  return { id: v.id, module: v.module, name: str(v.name) || `${MODULE_BY_KEY[v.module].label} play`, status, audience: readAudience(v.audience) };
}

export function readPlay(v: unknown): Play | null {
  const s = readPlaySummary(v);
  if (!s || !isObj(v)) return null;
  const angles = (Array.isArray(v.angles) ? v.angles : []).filter(isObj).map((a) => ({ key: str(a.key), label: str(a.label), line: str(a.line) })).filter((a) => a.key && a.line);
  return { ...s, card: isObj(v.card) ? v.card : null, angles, cold_test: isObj(v.cold_test) ? v.cold_test : null, approved_at: str(v.approved_at) || null, created_at: str(v.created_at) || undefined,
    updated_at: str(v.updated_at) || undefined, mirrors_settings: v.mirrors_settings === true, decision_note: str(v.decision_note) || null,
    signals: readPlaySignals(v.signals) };
}

export function readRoute(v: unknown): RouteChoice | null {
  if (!isObj(v) || !isModuleKey(v.first_module)) return null;
  return { goal: typeof v.goal === "string" && v.goal.trim() ? v.goal : null, first_module: v.first_module, source: v.source === "owner" ? "owner" : "suggested", reason: str(v.reason) };
}

/** crm_setup_state's answer, with every module and part present (a module the server leaves out is "later"). */
export function readSetupState(raw: unknown): SetupView {
  const r = isObj(raw) ? raw : {};
  const mods = isObj(r.modules) ? r.modules : {};
  const modules = {} as Record<ModuleKey, ModuleView>;
  for (const m of MODULES) {
    const mv = isObj(mods[m.key]) ? (mods[m.key] as JsonObject) : {};
    const parts: Record<string, PartView> = {};
    const rawParts = isObj(mv.parts) ? mv.parts : {};
    for (const part of m.parts) {
      const pv = rawParts[part.key];
      if (isObj(pv)) parts[part.key] = { on: pv.on === true, status: PART_STATUSES.includes(pv.status as PartStatus) ? (pv.status as PartStatus) : "not_started" };
      else parts[part.key] = { on: typeof pv === "boolean" ? pv : false, status: "not_started" };
    }
    const state = STATES.includes(mv.state as ModuleState) ? (mv.state as ModuleState) : m.key === "connect" || r.legacy === true ? "on" : "later";
    modules[m.key] = { state, reason: str(mv.reason), parts, suggested: mv.suggested === true, decided: mv.decided === true };
  }
  const plays = (Array.isArray(r.plays) ? r.plays : []).map(readPlaySummary).filter((x): x is PlaySummary => Boolean(x));
  const core = readCore(r.core);
  const view: SetupView = {
    core,
    core_confirmed: r.core_confirmed === true || Boolean(core.confirmed_at),
    route: readRoute(r.route),
    modules,
    plays,
    next: { step: "core", module: null, part: null },
    live: r.live === true,
    legacy: r.legacy === true,
  };
  view.route_confirmed = r.route_confirmed === true || (r.route_confirmed === undefined && view.route?.source === "owner");
  view.priority = isPriorityKey(r.priority) ? r.priority : null;
  const ps = isObj(r.priority_suggestion) ? r.priority_suggestion : null;
  view.priority_suggestion = ps && isPriorityKey(ps.priority) ? { priority: ps.priority, reason: str(ps.reason), by: ps.by === "claude" ? "claude" : "rules" } : null;
  const j = isObj(r.journey) ? r.journey : null;
  const jSteps = ["core", "priority", "connect", "running", "done"];
  view.journey = j && jSteps.includes(j.step as string)
    ? { step: j.step as Journey["step"], module: isModuleKey(j.module) ? j.module : null }
    : computeJourney(view, { wentLive: false });
  const n = isObj(r.next) ? r.next : null;
  const steps: SetupStepKey[] = ["core", "route", "modules", "play", "parts", "live"];
  view.next =
    n && steps.includes(n.step as SetupStepKey)
      ? { step: n.step as SetupStepKey, module: isModuleKey(n.module) ? n.module : null, part: typeof n.part === "string" ? n.part : null }
      : computeNext(view, { modulesConfirmed: Boolean(view.route) });
  return view;
}

// ---------------------------------------------------------------------------- where to go next
/** The modules in the order set-up takes them: the route's first module, then the rest in catalogue order. */
export function moduleOrder(v: Pick<SetupView, "route">): ModuleKey[] {
  const first = v.route?.first_module;
  return first ? [first, ...MODULE_KEYS.filter((m) => m !== first)] : [...MODULE_KEYS];
}

export function approvedPlay(v: Pick<SetupView, "plays">, m: ModuleKey): PlaySummary | null {
  return v.plays.find((x) => x.module === m && x.status === "approved") ?? null;
}

/** The newest play for a module that is not retired (the one the Play step works on). */
export function currentPlay(v: Pick<SetupView, "plays">, m: ModuleKey): PlaySummary | null {
  const mine = v.plays.filter((x) => x.module === m && x.status !== "retired");
  return mine.find((x) => x.status === "approved") ?? mine[mine.length - 1] ?? null;
}

/** Modules with a play of their own (Attract, Find, Network, ReConnect). Connect works from the others' plays, so it
 *  is never a first module and switching only Connect on is not enough to continue. */
export function playModulesOn(v: Pick<SetupView, "modules">): ModuleKey[] {
  return MODULE_KEYS.filter((m) => MODULE_BY_KEY[m].hasPlay && v.modules[m].state === "on");
}

/** The on modules that still need a play approved, in set-up order. */
export function modulesNeedingPlay(v: Pick<SetupView, "route" | "modules" | "plays">): ModuleKey[] {
  return moduleOrder(v).filter((m) => MODULE_BY_KEY[m].hasPlay && v.modules[m].state === "on" && !approvedPlay(v, m));
}

/** The switched-on parts of a module that are not set up yet. */
export function partsToSetUp(v: Pick<SetupView, "modules">, m: ModuleKey): string[] {
  const mod = v.modules[m];
  if (mod.state !== "on") return [];
  return MODULE_BY_KEY[m].parts.filter((p) => mod.parts[p.key]?.on && mod.parts[p.key]?.status === "not_started").map((p) => p.key);
}

/**
 * Where set-up goes next, worked out on the client when the server doesn't say (the demo, an older database):
 * the core, the route, the modules, the first module's play and parts, then live. Once the business has gone live,
 * each further module switched on starts again at its own play.
 */
export function computeNext(v: Omit<SetupView, "next">, opts: { modulesConfirmed: boolean; wentLive?: boolean }): NextStep {
  if (!v.core_confirmed) return { step: "core", module: null, part: null };
  if (!v.route) return { step: "route", module: null, part: null };
  if (!opts.modulesConfirmed || !playModulesOn(v).length) return { step: "modules", module: null, part: null };
  const first = v.route.first_module;
  const startWith = v.modules[first].state === "on" ? first : (moduleOrder(v).find((m) => v.modules[m].state === "on" && MODULE_BY_KEY[m].hasPlay) ?? first);
  if (MODULE_BY_KEY[startWith].hasPlay && v.modules[startWith].state === "on" && !approvedPlay(v, startWith)) return { step: "play", module: startWith, part: null };
  const todo = partsToSetUp(v, startWith);
  if (!opts.wentLive) return todo.length ? { step: "parts", module: startWith, part: todo[0]! } : { step: "live", module: null, part: null };
  const more = modulesNeedingPlay(v)[0];
  if (more) return { step: "play", module: more, part: null };
  return { step: "live", module: null, part: null };
}

/**
 * Where the four-step journey is (set-up v2), worked out on the client when the server doesn't say (the demo, an older
 * database): the core, the priority, connecting (until the first module has a play), then off and running.
 */
export function computeJourney(v: Omit<SetupView, "next" | "journey">, opts: { wentLive: boolean }): Journey {
  const first = v.route_confirmed && v.route && v.route.first_module !== "connect" ? v.route.first_module : null;
  if (opts.wentLive || v.legacy) return { step: "done", module: first };
  if (!v.core_confirmed) return { step: "core", module: null };
  if (!first || v.modules[first].state !== "on") return { step: "priority", module: null };
  if (!v.plays.some((p) => p.module === first && p.status !== "retired")) return { step: "connect", module: first };
  return { step: "running", module: first };
}

/** The address a step lives at. */
export function stepPath(step: SetupStepKey, module?: ModuleKey | null): string {
  if (step === "play" || step === "parts") return `/setup/${step}/${module ?? "find"}`;
  return `/setup/${step}`;
}

/** Where set-up resumes: the journey's step (set-up v2); once done, the older screens' next step. */
export function resumePath(v: Pick<SetupView, "next" | "route" | "journey">): string {
  if (v.journey && v.journey.step !== "done") return stepPath(v.journey.step);
  const m = v.next.module ?? v.route?.first_module ?? "find";
  return stepPath(v.next.step, m);
}

const JOURNEY_ORDER: JourneyStepKey[] = ["core", "priority", "connect", "running"];

/** The journey's four steps: done, now or still to do. */
export function journeyMarks(v: Pick<SetupView, "journey">): Record<JourneyStepKey, StepMark> {
  const at = v.journey?.step === "done" ? JOURNEY_ORDER.length : JOURNEY_ORDER.indexOf((v.journey?.step ?? "core") as JourneyStepKey);
  const out = {} as Record<JourneyStepKey, StepMark>;
  JOURNEY_ORDER.forEach((s, i) => {
    out[s] = i < at ? "done" : i === at ? "now" : "todo";
  });
  return out;
}

/** A journey step can be opened up to where the journey is; every step once done. */
export function canOpenJourney(v: Pick<SetupView, "journey">, step: JourneyStepKey): boolean {
  const s = v.journey?.step ?? "core";
  if (s === "done") return true;
  // Connecting can be skipped: off and running opens from it (arriving there drafts the first play).
  if (s === "connect" && step === "running") return true;
  return JOURNEY_ORDER.indexOf(step) <= JOURNEY_ORDER.indexOf(s);
}

export type StepMark = "done" | "now" | "todo";

const ORDER: SetupStepKey[] = ["core", "route", "modules", "play", "parts", "live"];

/** Each of the six steps: done, now (where set-up would resume), or still to do. */
export function stepMarks(v: SetupView): Record<SetupStepKey, StepMark> {
  const at = ORDER.indexOf(v.next.step);
  const out = { ...journeyMarks(v) } as Record<SetupStepKey, StepMark>;
  ORDER.forEach((s, i) => {
    if (s !== "core") out[s] = i < at ? "done" : i === at ? "now" : "todo";
  });
  return out;
}

/**
 * Steps a person may open: any step up to where set-up would resume; Go live once a play is approved; every step
 * once the business is live (onboarded).
 */
export function canOpen(v: SetupView, step: SetupStepKey, onboarded = false): boolean {
  if (onboarded) return true;
  if ((JOURNEY_ORDER as string[]).includes(step)) return canOpenJourney(v, step as JourneyStepKey);
  if (step === "live" && v.plays.some((x) => x.status === "approved")) return true;
  return ORDER.indexOf(step) <= Math.max(ORDER.indexOf(v.next.step), v.core_confirmed ? 2 : 0);
}

// ---------------------------------------------------------------------------- the system map
export interface Here {
  core?: boolean;
  module?: ModuleKey | null;
  part?: string | null;
  /** On the Modules step every module is in play. */
  allModules?: boolean;
}

/** "You are here" for a step. */
export function hereFor(step: SetupStepKey, module?: ModuleKey | null, part?: string | null): Here {
  if (step === "core") return { core: true };
  if (step === "priority" || step === "connect" || step === "running") return { module: module ?? null };
  if (step === "route") return { module: module ?? null };
  if (step === "modules") return { allModules: true };
  if (step === "live") return {};
  return { module: module ?? null, part: part ?? null };
}

export type MapTone = "on" | "later" | "off";

/** How a module is drawn: solid when on, dashed when later or off (still readable). */
export function moduleTone(mv: ModuleView): MapTone {
  return mv.state;
}

/** How a part is drawn: off (dashed), or on with its status lighting it up. */
export function partTone(mv: ModuleView, part: string): "off" | PartStatus {
  const pv = mv.parts[part];
  if (mv.state !== "on" || !pv?.on) return "off";
  return pv.status;
}

/** Plain-words list of the setup draft's module suggestions, for a change summary. */
export function moduleSummary(v: Pick<SetupView, "modules">): string {
  return MODULE_KEYS.map((m) => `${MODULE_BY_KEY[m].label}: ${v.modules[m].state}`).join(", ");
}

export function jsonList(v: Json | undefined): string[] {
  return strs(v);
}
